import { getInitialAppInstallationDataUrl } from '@wix/app-installer/internal-use';
import { useEffect, useState } from 'react';
import { getErrorPropsByServerlessErrorCode, INSTALLER_ERRORS } from '../errors';
import { getIsAdvanceOauthApp, isExternalDashboardApp } from '../selectors/appRuntimeReader';
import { useHandleError } from '../components/ErrorBoundary/ErrorHandlerProvider';
import { usePanorama } from '../context/PanoramaContext';
import { updateBiLoggerDefaults } from '../bi/bi';
export const usePreInstallationData = _ref => {
  let {
    appId,
    httpClient,
    tenantId,
    version,
    shareUrlId,
    preInstallationDataCache,
    isOneStepInstall,
    noAuthHeader
  } = _ref;
  const {
    handleError
  } = useHandleError();
  const {
    startTransaction,
    finishTransaction
  } = usePanorama();
  const [installationData, setInstallationData] = useState(undefined);
  useEffect(() => {
    if (appId && tenantId) {
      let requestId;
      startTransaction('pre-installation-data');
      (async () => {
        if (preInstallationDataCache) {
          const {
            appId: cachedAppId,
            tenantId: cachedTenantId,
            version: cachedVersion,
            preFetchPromise
          } = preInstallationDataCache;
          if (cachedAppId === appId && cachedTenantId === tenantId && cachedVersion === version) {
            return preFetchPromise;
          }
        }
        return httpClient.get(getInitialAppInstallationDataUrl({
          appId,
          version,
          tenantId,
          shareUrlId,
          isOneStepInstall
        }));
      })().then(res => {
        setInstallationData(res.data);
        const app = res.data.appRuntimeData;
        updateBiLoggerDefaults({
          is_advance_oauth: getIsAdvanceOauthApp(app),
          is_external_dashboard: isExternalDashboardApp(app),
          is_app_already_installed: Boolean(res.data.isAppAlreadyInstalled)
        });
        requestId = res.requestId;
      }).catch(error => {
        const errorProps = getErrorPropsByServerlessErrorCode(error, INSTALLER_ERRORS.DEFAULT_FETCH_PREINSTALL_DATA);
        handleError(errorProps);
      }).finally(() => {
        finishTransaction('pre-installation-data', {
          requestId
        });
      });
    }
  }, [tenantId]);
  const app = installationData == null ? void 0 : installationData.appRuntimeData;
  return {
    app,
    isAppCompatible: (installationData == null ? void 0 : installationData.isAppCompatible) ?? true,
    shouldShowBlocksLegacyConsent: (installationData == null ? void 0 : installationData.shouldShowBlocksLegacyConsent) ?? false,
    appForInstallation: installationData == null ? void 0 : installationData.appForInstallation,
    isPremiumSite: (installationData == null ? void 0 : installationData.isPremiumSite) || false,
    isSiteConnectedToDomain: (installationData == null ? void 0 : installationData.isSiteConnectedToDomain) || false,
    marketListing: installationData == null ? void 0 : installationData.marketListing,
    companyInfo: installationData == null ? void 0 : installationData.companyInfo,
    isExternalDashboard: isExternalDashboardApp(app),
    isAdvancedOauthApp: getIsAdvanceOauthApp(app),
    consentPermissions: (installationData == null ? void 0 : installationData.consentPermissions) || [],
    authorizationHeader: noAuthHeader ? undefined : installationData == null ? void 0 : installationData.authorizationHeader,
    isAppAlreadyInstalled: Boolean(installationData == null ? void 0 : installationData.isAppAlreadyInstalled)
  };
};
export const emptyPreInstallationData = {
  app: undefined,
  appForInstallation: undefined,
  isSiteConnectedToDomain: false,
  isPremiumSite: false,
  companyInfo: undefined,
  marketListing: undefined,
  isExternalDashboard: false,
  isAdvancedOauthApp: false,
  shouldShowBlocksLegacyConsent: false,
  authorizationHeader: undefined,
  isAppCompatible: true,
  consentPermissions: [],
  isAppAlreadyInstalled: false
};
//# sourceMappingURL=usePreInstallationData.js.map